/** Prompt bar form state → POST /generate body, and Remix settings → POST /trends/audio/{id}/lookalike body. Pure, so it is unit-tested. */
import type { DurationHint, GenerateBody, LookalikeBody, PresetTemplate, StudioPresets, Tempo } from '@/api/audioStudioApi';

export interface CreateFormState {
  prompt: string;
  presetId: string | null;
  genre: string;
  mood: string;
  tempo: Tempo | '';
  energy: number;
  instruments: string[];
  vocals: boolean;
  lyrics: string;
  duration: DurationHint;
  useCase: string;
  variations: number;
  title: string;
}

export const INITIAL_CREATE_FORM: CreateFormState = {
  prompt: '',
  presetId: null,
  genre: '',
  mood: '',
  tempo: '',
  energy: 50,
  instruments: [],
  vocals: false,
  lyrics: '',
  duration: 30,
  // Ads-first default: a bed that sits under the creator talking to camera.
  useCase: 'voiceover_bed',
  variations: 1,
  title: '',
};

export const PROMPT_MAX = 1000;
/** The lookalike endpoint caps `extra_prompt` at 500 characters. */
export const LOOKALIKE_PROMPT_MAX = 500;

/** Used until GET /presets answers (or if it fails) so the form is never empty. */
export const FALLBACK_PRESETS: StudioPresets = {
  genres: ['pop', 'hip-hop', 'electronic', 'house', 'lo-fi', 'rock', 'indie', 'r&b', 'afrobeats', 'latin', 'cinematic', 'acoustic', 'jazz', 'funk'].map((id) => ({ id, label: id === 'r&b' ? 'R&B' : id.charAt(0).toUpperCase() + id.slice(1) })),
  moods: ['upbeat', 'energetic', 'happy', 'chill', 'dreamy', 'emotional', 'confident', 'playful', 'dark', 'epic'].map((id) => ({ id, label: id.charAt(0).toUpperCase() + id.slice(1) })),
  tempos: [{ id: 'slow', label: 'Slow' }, { id: 'medium', label: 'Medium' }, { id: 'fast', label: 'Fast' }],
  energies: [],
  instruments: ['piano', 'acoustic guitar', 'electric guitar', 'synth', 'bass', 'drums', '808', 'strings', 'brass', 'claps'].map((id) => ({ id, label: id.charAt(0).toUpperCase() + id.slice(1) })),
  durations: [{ id: '6', label: '6s' }, { id: '15', label: '15s' }, { id: '30', label: '30s' }, { id: '60', label: '60s' }],
  use_cases: [
    { id: 'voiceover_bed', label: 'Voice-over bed' },
    { id: 'tiktok', label: 'TikTok / Reels' },
    { id: 'product_demo', label: 'Product demo' },
    { id: 'unboxing', label: 'Unboxing' },
    { id: 'tutorial', label: 'Tutorial' },
    { id: 'brand_story', label: 'Brand story' },
    { id: 'podcast_intro', label: 'Podcast intro' },
  ],
  templates: [],
};

export function energyLabel(energy: number): string {
  return energy < 34 ? 'Calm' : energy < 67 ? 'Balanced' : 'High';
}

export function isTempo(v: unknown): v is Tempo {
  return v === 'slow' || v === 'medium' || v === 'fast';
}

export function isDurationHint(v: unknown): v is DurationHint {
  return v === 6 || v === 15 || v === 30 || v === 60;
}

/** Selecting a template chip: its values fill the controls (the prompt stays the user's). */
export function applyTemplate(state: CreateFormState, tpl: PresetTemplate): CreateFormState {
  const v = tpl.values || {};
  return {
    ...state,
    presetId: tpl.id,
    genre: v.genre ?? state.genre,
    mood: v.mood ?? state.mood,
    tempo: isTempo(v.tempo) ? v.tempo : state.tempo,
    energy: typeof v.energy === 'number' ? Math.max(0, Math.min(100, v.energy)) : state.energy,
    instruments: Array.isArray(v.instruments) ? [...v.instruments] : state.instruments,
    vocals: v.vocals ? v.vocals === 'vocals' : state.vocals,
    lyrics: v.lyrics ?? state.lyrics,
    duration: isDurationHint(v.duration_hint_s) ? v.duration_hint_s : state.duration,
    useCase: v.use_case ?? state.useCase,
  };
}

/** The contract needs at least one of prompt / preset / genre / mood. */
export function canGenerate(s: CreateFormState): boolean {
  return Boolean(s.prompt.trim() || s.presetId || s.genre || s.mood);
}

export function buildGenerateBody(s: CreateFormState): GenerateBody {
  const body: GenerateBody = {
    variations: Math.max(1, Math.min(3, Math.round(s.variations || 1))),
    energy: Math.max(0, Math.min(100, Math.round(s.energy))),
    instruments: s.instruments,
    vocals: s.vocals ? 'vocals' : 'instrumental',
    duration_hint_s: s.duration,
  };
  const prompt = s.prompt.trim().slice(0, PROMPT_MAX);
  if (prompt) body.prompt = prompt;
  if (s.presetId) body.preset_id = s.presetId;
  if (s.genre) body.genre = s.genre;
  if (s.mood) body.mood = s.mood;
  if (s.tempo) body.tempo = s.tempo;
  if (s.useCase) body.use_case = s.useCase;
  if (s.title.trim()) body.title = s.title.trim();
  if (s.vocals && s.lyrics.trim()) body.lyrics = s.lyrics.trim();
  return body;
}

/** What the Remix dialog asks for: length, vocals and optional extra instructions. */
export interface RemixSettings {
  duration: DurationHint;
  vocals: boolean;
  extraPrompt: string;
}

/**
 * Lookalike (Remix) body: length and vocals, plus the typed instructions as `extra_prompt` (≤ 500 chars, omitted when
 * empty). Genre / mood / tempo / instruments come from the trend itself, so they are never sent.
 */
export function buildLookalikeBody(s: RemixSettings): LookalikeBody {
  const body: LookalikeBody = {
    variations: 1,
    duration_hint_s: s.duration,
    vocals: s.vocals ? 'vocals' : 'instrumental',
  };
  const extra = s.extraPrompt.trim().slice(0, LOOKALIKE_PROMPT_MAX);
  if (extra) body.extra_prompt = extra;
  return body;
}

/** Ad lengths offered in the prompt bar (the service accepts 6 / 15 / 30 / 60). */
export const AD_DURATIONS: readonly DurationHint[] = [6, 15, 30, 60];

/** The use case offered first as a quick choice (and the default for new users). */
export const VOICEOVER_BED = 'voiceover_bed';

/** The prompt-bar settings remembered between visits (length, vocals, use case). */
export const SETTINGS_STORAGE_KEY = 'beatly.audioStudio.promptSettings.v1';
type StoredSettings = Pick<CreateFormState, 'duration' | 'vocals' | 'useCase'>;

/** The initial form: the defaults, overlaid with the user's last length / vocals / use case when stored. */
export function loadInitialForm(storage: Pick<Storage, 'getItem'> | null = safeLocalStorage()): CreateFormState {
  try {
    const raw = storage?.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return INITIAL_CREATE_FORM;
    const v = JSON.parse(raw) as Partial<StoredSettings>;
    return {
      ...INITIAL_CREATE_FORM,
      duration: isDurationHint(v.duration) ? v.duration : INITIAL_CREATE_FORM.duration,
      vocals: typeof v.vocals === 'boolean' ? v.vocals : INITIAL_CREATE_FORM.vocals,
      useCase: typeof v.useCase === 'string' ? v.useCase : INITIAL_CREATE_FORM.useCase,
    };
  } catch {
    return INITIAL_CREATE_FORM;
  }
}

export function saveSettings(s: CreateFormState, storage: Pick<Storage, 'setItem'> | null = safeLocalStorage()): void {
  try {
    const v: StoredSettings = { duration: s.duration, vocals: s.vocals, useCase: s.useCase };
    storage?.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(v));
  } catch {
    // Private mode / blocked storage: settings just aren't remembered.
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
